import { useMemo } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { latestReservoirState } from "@/lib/api/refill-sessions";
import type { RefillSession } from "@/lib/api/types";

// GrowHistoryChart plots the post-pour pH + EC result of each refill across
// time, so a grower can see at a glance whether the reservoir is trending
// toward or away from the target band. Sessions arrive newest-first (the API
// sorts started_at DESC); we reverse to chronological order for the X axis.
// EC is plotted in mS/cm (the storage unit) to keep the axis domain stable
// regardless of the user's display-unit preference — this is a trend view,
// not a precise readout.

type Datum = {
  label: string;
  ph?: number;
  ec?: number;
};

export function GrowHistoryChart({ sessions }: { sessions: RefillSession[] }) {
  const data = useMemo<Datum[]>(
    () =>
      [...sessions]
        .filter((s) => !s.voided_at)
        .reverse()
        .map<Datum>((s) => {
          const st = latestReservoirState(s);
          const when = s.closed_at ?? s.started_at;
          return {
            label: shortDate(when),
            ph: st?.ph,
            ec: st?.ec,
          };
        })
        .filter((d) => d.ph !== undefined || d.ec !== undefined),
    [sessions],
  );

  if (data.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        No refill history yet — the chart fills in as you log refills.
      </p>
    );
  }

  return (
    <div className="h-40 w-full">
      <ResponsiveContainer>
        <LineChart data={data} margin={{ top: 8, right: 4, bottom: 0, left: -8 }}>
          <CartesianGrid strokeOpacity={0.15} />
          <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
          <YAxis
            yAxisId="ph"
            domain={[4.5, 8.5]}
            ticks={[5, 6, 7, 8]}
            tick={{ fontSize: 10 }}
            width={24}
          />
          <YAxis
            yAxisId="ec"
            orientation="right"
            domain={[0, 3]}
            ticks={[0, 1, 2, 3]}
            tick={{ fontSize: 10 }}
            width={20}
          />
          <Tooltip
            wrapperClassName="text-xs"
            contentStyle={{
              background: "var(--popover)",
              border: "1px solid var(--border)",
              borderRadius: 6,
              color: "var(--popover-foreground)",
            }}
            labelStyle={{ fontWeight: 500 }}
          />
          <Line
            yAxisId="ph"
            type="monotone"
            dataKey="ph"
            stroke="#7c3aed"
            dot={{ r: 2 }}
            name="pH"
            connectNulls
          />
          <Line
            yAxisId="ec"
            type="monotone"
            dataKey="ec"
            stroke="#059669"
            dot={{ r: 2 }}
            name="EC"
            connectNulls
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function shortDate(s: string): string {
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
